import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import { Calendar, Package } from 'lucide';

// MUI
import { Box, Card, Stack, Typography } from '@mui/material';

// common
import Icon from 'common/components/Icon';
import ImageDialog from 'common/components/ImageDialog';
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import NotFoundBox from 'common/components/NotFoundBox';
import type { LogiReceivingRecord, LogiReceivingRecordPhotos } from 'api/apiClient';

interface Props {
  open: boolean;
  onClose: () => void;
  isFetching: boolean;
  records: LogiReceivingRecord[];
}

const collectPhotoUrls = (photos?: LogiReceivingRecordPhotos | null): string[] => {
  if (!photos) return [];
  const urls: (string | null | undefined)[] = [
    photos.product,
    photos.delivery_note,
    photos.qr_page_screenshot,
    photos.qr_label,
    ...(photos.extra ?? []),
  ];
  return urls.filter((u): u is string => typeof u === 'string' && u.length > 0);
};

const FoodReceivingRecordsDialog = (props: Props) => {
  const { open, onClose, isFetching, records } = props;

  const renderContent = () => {
    if (isFetching) return <MuiLoading />;
    if (!records.length) return <NotFoundBox />;
    return (
      <Stack spacing={2} className="p-2 tablet:p-4">
        {records.map((rec) => {
          const urls = collectPhotoUrls(rec.photos);
          return (
            <Card key={rec.id} sx={{ p: 2 }} variant="outlined">
              <Typography variant="subtitle1" color="error" fontWeight={700} mb={1}>
                {rec.supplier_name || '—'}
              </Typography>
              <Box
                className="flex flex-wrap items-center mb-2"
                sx={{ gap: 2, color: 'text.secondary' }}
              >
                {rec.product_name && (
                  <Box className="flex items-center gap-1">
                    <Icon icon={Package} size={16} />
                    <Typography variant="body2">{rec.product_name}</Typography>
                  </Box>
                )}
                {rec.quantity && (
                  <Typography variant="body2">SL: {rec.quantity}</Typography>
                )}
                {rec.recorded_at && (
                  <Box className="flex items-center gap-1">
                    <Icon icon={Calendar} size={16} />
                    <Typography variant="body2">
                      {dayjs(rec.recorded_at).format('DD/MM/YYYY')}
                    </Typography>
                  </Box>
                )}
              </Box>
              {urls.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  <FormattedMessage id="map.foodTraceability.noPhotos" />
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, 1fr)',
                      tablet: 'repeat(3, 1fr)',
                      desktop: 'repeat(5, 1fr)',
                    },
                    gap: 1,
                  }}
                >
                  {urls.map((url, idx) => (
                    <Box
                      key={`${rec.id}-${idx}`}
                      sx={{
                        aspectRatio: '1 / 1',
                        '& > .MuiAvatar-root': {
                          width: '100% !important',
                          height: '100% !important',
                        },
                        '& img': {
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        },
                      }}
                    >
                      <ImageDialog src={url} alt={rec.product_name || ''} />
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          );
        })}
      </Stack>
    );
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      disablePortal
      titleAlign="center"
      title={'map.foodTraceability.foodReceiptImages'}
      PaperProps={{
        sx: {
          margin: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '90%',
          maxWidth: '90%',
          minHeight: 0,
        },
      }}
    >
      <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>{renderContent()}</Box>
    </MuiDialog>
  );
};

export default FoodReceivingRecordsDialog;
